
export interface CaseStudy {
  id: string;
  slug: string;
  companyKey: string;
  industryKey: string;
  challengeKey: string;
  solutionKey: string;
  results: { metricKey: string; labelKey: string }[];
  testimonialKey: string;
  durationKey: string;
  logo: string;
  image: string;
  content: string;
}

export const caseStudies: CaseStudy[] = [
  {
    id: 'item1',
    slug: 'dr-weidinger-arztpraxis',
    companyKey: 'caseStudies.item1.company',
    industryKey: 'caseStudies.item1.industry',
    challengeKey: 'caseStudies.item1.challenge',
    solutionKey: 'caseStudies.item1.solution',
    results: [
      { metricKey: 'caseStudies.item1.results.res1.metric', labelKey: 'caseStudies.item1.results.res1.label' },
      { metricKey: 'caseStudies.item1.results.res2.metric', labelKey: 'caseStudies.item1.results.res2.label' },
      { metricKey: 'caseStudies.item1.results.res3.metric', labelKey: 'caseStudies.item1.results.res3.label' },
    ],
    testimonialKey: 'caseStudies.item1.testimonial',
    durationKey: 'caseStudies.item1.duration',
    logo: '/brand/app-icon.png',
    image: '/images/readdy/case1b.jpg',
    content: `
      <h2>Die Ausgangslage</h2>
      <p>Die Praxis von Dr. med. Klaus Weidinger ist seit über 15 Jahren in München etabliert. Trotz hervorragender medizinischer Versorgung spiegelte sich dies nicht in der Online-Reputation wider. Mit nur 12 Bewertungen in 3 Jahren (Durchschnitt 3.8 Sterne) war die Praxis im lokalen Wettbewerb kaum sichtbar. Viele zufriedene Patienten hinterließen kein Feedback, während vereinzelte negative Stimmen das Bild verzerrten.</p>
      
      <h3>Die Herausforderung</h3>
      <ul>
        <li>Kaum neue Bewertungen trotz hoher Patientenzahl</li>
        <li>Veraltetes Google-Unternehmensprofil</li>
        <li>Keine Zeit für manuelles Reputationsmanagement im Praxisalltag</li>
      </ul>

      <h2>Die Lösung mit reputrust.ai</h2>
      <p>In Zusammenarbeit mit reputrust.ai wurde ein automatisierter Prozess implementiert, der perfekt in den Praxisablauf passt, ohne das Personal zusätzlich zu belasten.</p>
      
      <h3>1. QR-Code Integration</h3>
      <p>Im Wartezimmer und am Empfang wurden Aufsteller mit einem speziellen QR-Code platziert. Patienten können so während der Wartezeit oder direkt nach der Behandlung ihr Feedback abgeben.</p>

      <h3>2. Automatisierte E-Mail-Kampagnen</h3>
      <p>Das Praxissystem wurde via API mit reputrust.ai verbunden. 24 Stunden nach dem Termin erhalten Patienten automatisch eine freundliche E-Mail mit der Bitte um Bewertung – natürlich voll DSGVO-konform.</p>

      <h3>3. Sentiment-Filter</h3>
      <p>Um negatives Feedback intern zu klären, bevor es öffentlich wird, wurde ein Sentiment-Filter vorgeschaltet. Patienten, die weniger als 4 Sterne geben würden, werden auf ein internes Formular geleitet. Zufriedene Patienten werden direkt zu Google weitergeleitet.</p>

      <h2>Die Ergebnisse</h2>
      <p>Innerhalb von nur 6 Monaten hat sich das Online-Bild der Praxis komplett gewandelt. Die Anzahl der Bewertungen stieg explosionsartig an, was sich direkt auf das lokale Google-Ranking auswirkte. Dr. Weidinger erscheint nun bei Suchanfragen wie "Hausarzt München Zentrum" konstant in den Top 3.</p>
    `
  },
  {
    id: 'item2',
    slug: 'restaurant-bella-vista',
    companyKey: 'caseStudies.item2.company',
    industryKey: 'caseStudies.item2.industry',
    challengeKey: 'caseStudies.item2.challenge',
    solutionKey: 'caseStudies.item2.solution',
    results: [
      { metricKey: 'caseStudies.item2.results.res1.metric', labelKey: 'caseStudies.item2.results.res1.label' },
      { metricKey: 'caseStudies.item2.results.res2.metric', labelKey: 'caseStudies.item2.results.res2.label' },
      { metricKey: 'caseStudies.item2.results.res3.metric', labelKey: 'caseStudies.item2.results.res3.label' },
    ],
    testimonialKey: 'caseStudies.item2.testimonial',
    durationKey: 'caseStudies.item2.duration',
    logo: '/brand/app-icon.png',
    image: '/images/readdy/case2b.jpg',
    content: `
      <h2>Die Ausgangslage</h2>
      <p>Das Restaurant "Bella Vista" kämpfte mit einem klassischen Problem der Gastronomie: Zufriedene Gäste schweigen und genießen, während unzufriedene Gäste ihren Ärger online Luft machen. Zudem fehlte dem Team im stressigen Tagesgeschäft die Zeit, auf Bewertungen zu antworten.</p>
      
      <h2>Die Strategie</h2>
      <p>Das Ziel war klar: Die "stille Mehrheit" der glücklichen Gäste zu aktivieren und gleichzeitig jede eingehende Bewertung professionell zu beantworten.</p>

      <h3>AI-Power für Antworten</h3>
      <p>Der reputrust.ai AI-Reply-Bot wurde so konfiguriert, dass er auf 4- und 5-Sterne-Bewertungen automatisch, aber individuell und herzlich antwortet. Bei 1-3 Sterne-Bewertungen erstellt die AI einen Antwortentwurf, der deeskalierend wirkt und das Gespräch offline lenkt. Der Geschäftsführer muss diesen nur noch kurz prüfen und freigeben.</p>

      <h3>Tischaufsteller mit NFC</h3>
      <p>Auf jedem Tisch wurden elegante Aufsteller mit NFC-Chips platziert. Gäste müssen nur ihr Smartphone daran halten, um das Restaurant zu bewerten – noch während sie auf die Rechnung warten.</p>

      <h2>Das Ergebnis</h2>
      <p>Die Antwortrate stieg von 15% auf 100%. Gäste fühlen sich wertgeschätzt, was viele dazu motiviert, wiederzukommen. Durch die vielen positiven Signale kletterte das Restaurant bei TripAdvisor auf Platz 5 der beliebtesten Italiener der Stadt.</p>
    `
  },
  {
    id: 'item3',
    slug: 'immobilien-weber',
    companyKey: 'caseStudies.item3.company',
    industryKey: 'caseStudies.item3.industry',
    challengeKey: 'caseStudies.item3.challenge',
    solutionKey: 'caseStudies.item3.solution',
    results: [
      { metricKey: 'caseStudies.item3.results.res1.metric', labelKey: 'caseStudies.item3.results.res1.label' },
      { metricKey: 'caseStudies.item3.results.res2.metric', labelKey: 'caseStudies.item3.results.res2.label' },
      { metricKey: 'caseStudies.item3.results.res3.metric', labelKey: 'caseStudies.item3.results.res3.label' },
    ],
    testimonialKey: 'caseStudies.item3.testimonial',
    durationKey: 'caseStudies.item3.duration',
    logo: '/brand/app-icon.png',
    image: '/images/readdy/case3b.jpg',
    content: `
      <h2>Die Ausgangslage</h2>
      <p>Die Immobilien Weber GmbH hatte zwar exzellente Kundenbewertungen auf Google, nutzte dieses Potenzial aber nicht für das Marketing. Die Social-Media-Kanäle (Facebook & Instagram) wurden nur sporadisch mit Exposés bespielt und hatten wenig Engagement.</p>
      
      <h2>Die Lösung: Content-Automation</h2>
      <p>Mit der "Social Proof Engine" von reputrust.ai wurde der Prozess automatisiert.</p>

      <h3>Vom Feedback zum Post</h3>
      <p>Sobald eine 5-Sterne-Bewertung eingeht, generiert reputrust.ai automatisch eine Grafik im Corporate Design der Immobilien Weber GmbH. Diese Grafik enthält das Zitat des Kunden, die Sterne und das Firmenlogo.</p>

      <h3>Multi-Channel-Publishing</h3>
      <p>Die generierten Posts werden automatisch zu den Zeiten geplant, an denen die Zielgruppe am aktivsten ist. LinkedIn wird für B2B-Kontakte (Gewerbeimmobilien) genutzt, während Instagram und Facebook private Käufer und Verkäufer ansprechen.</p>

      <h2>Die Ergebnisse</h2>
      <p>Das Vertrauen in die Marke stieg spürbar. Potenzielle Verkäufer meldeten sich proaktiv, weil sie "die vielen guten Erfahrungen anderer Kunden auf Facebook gesehen haben". Der Social Proof wurde zum stärksten Vertriebsargument.</p>
    `
  },
  {
    id: 'item4',
    slug: 'autohaus-mueller',
    companyKey: 'caseStudies.item4.company',
    industryKey: 'caseStudies.item4.industry',
    challengeKey: 'caseStudies.item4.challenge',
    solutionKey: 'caseStudies.item4.solution',
    results: [
      { metricKey: 'caseStudies.item4.results.res1.metric', labelKey: 'caseStudies.item4.results.res1.label' },
      { metricKey: 'caseStudies.item4.results.res2.metric', labelKey: 'caseStudies.item4.results.res2.label' },
      { metricKey: 'caseStudies.item4.results.res3.metric', labelKey: 'caseStudies.item4.results.res3.label' },
    ],
    testimonialKey: 'caseStudies.item4.testimonial',
    durationKey: 'caseStudies.item4.duration',
    logo: '/brand/app-icon.png',
    image: '/images/readdy/case4b.jpg',
    content: `
      <h2>Die Herausforderung</h2>
      <p>Das Autohaus Müller & Söhne betreibt 5 Standorte in der Region. Die Verwaltung der Google-Unternehmensprofile war chaotisch: Unterschiedliche Öffnungszeiten, veraltete Fotos und unbeantwortete Fragen. Es fehlte der zentrale Überblick.</p>
      
      <h2>Die Lösung: Central Command</h2>
      <p>Alle 5 Standorte wurden in das reputrust.ai Dashboard integriert. Dies ermöglichte eine zentrale Steuerung aller Aktivitäten.</p>

      <h3>Einheitlicher Markenauftritt</h3>
      <p>Fotos von neuen Modellen, Aktionsangebote und Events werden nun mit einem Klick auf allen 5 Profilen gleichzeitig veröffentlicht (Google Posts). Öffnungszeiten an Feiertagen werden zentral gesteuert und synchronisiert.</p>

      <h3>Vergleichbarkeit & Gamification</h3>
      <p>Ein internes Leaderboard zeigt, welcher Standort die beste Performance bei der Bewertungsgenerierung liefert. Dies spornte die Standortleiter an, den Service weiter zu verbessern.</p>

      <h2>Die Ergebnisse</h2>
      <p>Die Sichtbarkeit aller Standorte stieg summiert um über 200%. Besonders der Verkauf von Gebrauchtwagen profitierte durch die bessere Auffindbarkeit und das gestiegene Vertrauen durch aktuelle Bewertungen.</p>
    `
  },
  {
    id: 'item5',
    slug: 'fitness-studio-powergym',
    companyKey: 'caseStudies.item5.company',
    industryKey: 'caseStudies.item5.industry',
    challengeKey: 'caseStudies.item5.challenge',
    solutionKey: 'caseStudies.item5.solution',
    results: [
      { metricKey: 'caseStudies.item5.results.res1.metric', labelKey: 'caseStudies.item5.results.res1.label' },
      { metricKey: 'caseStudies.item5.results.res2.metric', labelKey: 'caseStudies.item5.results.res2.label' },
      { metricKey: 'caseStudies.item5.results.res3.metric', labelKey: 'caseStudies.item5.results.res3.label' },
    ],
    testimonialKey: 'caseStudies.item5.testimonial',
    durationKey: 'caseStudies.item5.duration',
    logo: '/brand/app-icon.png',
    image: '/images/readdy/case5b.jpg',
    content: `
      <h2>Die Ausgangslage</h2>
      <p>PowerGym hatte mit einer hohen Fluktuation zu kämpfen. Kündigungsgründe waren oft unklar. Online-Bewertungen waren rar und meist nur von extrem unzufriedenen Ex-Mitgliedern verfasst.</p>
      
      <h2>Die Lösung: Feedback-Kultur</h2>
      <p>PowerGym nutzte reputrust.ai, um einen kontinuierlichen Feedback-Loop zu etablieren.</p>

      <h3>Automatische Umfragen</h3>
      <p>Nach jedem 10. Check-in und nach jedem Probetraining erhalten Mitglieder automatisch eine kurze SMS mit der Frage: "Wie war dein Training heute?".</p>

      <h3>Kritik als Chance</h3>
      <p>Mitglieder, die Probleme melden (z.B. "Duschen waren nicht sauber"), erhalten sofort eine Entschuldigungs-SMS und einen Gutschein für einen Protein-Shake. Das Management wird alarmiert, um das Problem sofort zu beheben.</p>

      <h2>Die Ergebnisse</h2>
      <p>Die Mitglieder fühlten sich gehört. Die Kündigungsrate sank signifikant. Gleichzeitig stieg die Zahl der positiven Bewertungen, da zufriedene Mitglieder nun aktiv zur Bewertung aufgefordert wurden. PowerGym gilt heute als das "sauberste und freundlichste" Studio der Stadt.</p>
    `
  },
  {
    id: 'item6',
    slug: 'hotel-alpenblick',
    companyKey: 'caseStudies.item6.company',
    industryKey: 'caseStudies.item6.industry',
    challengeKey: 'caseStudies.item6.challenge',
    solutionKey: 'caseStudies.item6.solution',
    results: [
      { metricKey: 'caseStudies.item6.results.res1.metric', labelKey: 'caseStudies.item6.results.res1.label' },
      { metricKey: 'caseStudies.item6.results.res2.metric', labelKey: 'caseStudies.item6.results.res2.label' },
      { metricKey: 'caseStudies.item6.results.res3.metric', labelKey: 'caseStudies.item6.results.res3.label' },
    ],
    testimonialKey: 'caseStudies.item6.testimonial',
    durationKey: 'caseStudies.item6.duration',
    logo: '/brand/app-icon.png',
    image: '/images/readdy/case6b.jpg',
    content: `
      <h2>Die Herausforderung</h2>
      <p>Das Hotel Alpenblick war auf über 10 verschiedenen Plattformen präsent (Booking.com, Expedia, TripAdvisor, Google, Facebook, HolidayCheck, etc.). Der Aufwand, alle Portale zu überwachen und auf Bewertungen zu antworten, war enorm und fehleranfällig.</p>
      
      <h2>Die Lösung: Alles an einem Ort</h2>
      <p>Mit reputrust.ai wurden alle Plattformen in einem einzigen Dashboard gebündelt.</p>

      <h3>Zentraler Posteingang</h3>
      <p>Egal ob eine Bewertung auf Booking.com oder Google eingeht – alles landet im gleichen Feed. Das Rezeptionsteam kann direkt aus dem Dashboard antworten, ohne sich auf 10 verschiedenen Seiten einloggen zu müssen.</p>

      <h3>Urlaubs-Templates</h3>
      <p>Für saisonale Peaks wurden spezielle Antwort-Templates erstellt, die von der AI genutzt werden, um schnell und dennoch persönlich auf das Feedback der Gäste einzugehen.</p>

      <h2>Die Ergebnisse</h2>
      <p>Die Reaktionszeit auf Gästefeedback sank von durchschnittlich 5 Tagen auf unter 12 Stunden. Dies wird von den Algorithmen der Buchungsplattformen belohnt: Das Ranking verbesserte sich auf allen Portalen, was zu einem deutlichen Anstieg der Direktbuchungen führte.</p>
    `
  }
];
